import { AdapterContext } from './adapter-context';
import { IModelAdapter } from './model-adapter';
import { Metamodel } from '../base/model';
import { Named } from '../base/named';
import { ILogger } from '../../interfaces/ILogger';
import { LoadingStatusEnum } from '../../interfaces/IResourceFilesProvider';

export interface IInitializableAdapter {
    initContext(context: AdapterContext): void;
}

export const staticConsoleLogger = {
    log(componentName: string, message: string, properties?: Record<string, unknown>) {
        // noop
    },
    logEvent(event: unknown) {
        // noop
    },
    error(componentName: string, message: unknown, properties?: Record<string, unknown>) {
        // eslint-disable-next-line no-console
        console.error(componentName, message, properties);
    },
    warn(componentName: string, message: string, properties?: Record<string, unknown>) {
        // eslint-disable-next-line no-console
        console.warn(componentName, message, properties);
    }
};

/*
    (Grand)Mother of all adapters that require a context. Or the specific
    adapter has to have a initContext function
*/
export abstract class BaseAdapter<T, U, D = unknown> implements IInitializableAdapter, IModelAdapter<T, U, D> {
    // logger getter to replace the staticConsoleLogger when needed
    public static getCachedLoggerSync(_contextData: AdapterContext['contextData']): ILogger | undefined {
        // seize and return 'LoggerService'
        return undefined;
    }

    private static readonly className = 'BaseAdapter';
    protected context: AdapterContext;

    public initContext(context: AdapterContext): void {
        this.context = context;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public toResource(named: T, ...other: any[]): U {
        try {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            if (named && !(named as any).isOk) {
                // We do not want to save/publish bad resources.
                // Ideally, the control should not come over here because
                // bad resources are not editable.
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const resourceName = (named as any).name ? (named as any).name : '';
                throw new Error(`Unable to save ${resourceName}. Bad resource`);
            }
            const resource = this._toResource(named, ...other);
            if (this.context?.adapterConfig?.postProcessResource) {
                this.context.adapterConfig.postProcessResource(resource);
            }
            return resource;
        } catch (error) {
            const model = Metamodel.modelOf(named).name;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const name = (named as any).name;
            const errorStr = ''; // `toResource failed. Resason: ${convertObjectToString(error)}`;
            const logString = JSON.stringify({
                model,
                name,
                error: errorStr
            });
            this._logError(logString);
            throw new Error(error);
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public toModel(resource: U, ...other: any[]): T {
        try {
            if (this.context?.adapterConfig?.preProcessResource) {
                resource = { ...resource };
                this.context.adapterConfig.preProcessResource(resource);
            }
            return this._toModel(resource, ...other);
        } catch (error) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const aresource = resource as any;
            const resourceType = aresource.type;
            const name = aresource.name;
            const errorStr = ''; // `toModel failed. Resason: ${convertObjectToString(error)}`;
            const logString = JSON.stringify({
                resourceType,
                name,
                error: errorStr
            });
            this._logError(logString);

            // For pipeline, dataset, trigger, linkedservice and IR, the first item in 'other'
            // will be the named, which the respective adapter will fill-in and return.
            // A better approach would be to let the individual adapters return the bad resources.
            // But this would require changes in all the adapters, so settling with this for now.
            // If _toModel fails, then return a bad model.
            // linkedservice._toModel does not go through base.adapter, stil having check for safety.
            if (other && other.length) {
                const named = other[0] as Named;
                if (named) {
                    const model = Metamodel.modelOf(named);
                    if (model && model.concept && model.concept.name) {
                        if (
                            [
                                'pipeline',
                                'dataset',
                                'dataflow',
                                'datamapper',
                                'adfcdc',
                                'linkedservice',
                                'trigger',
                                'integrationruntime'
                            ].includes(model.concept.name.toLowerCase())
                        ) {
                            named.status = 'Bad';
                            named.loadError.status = LoadingStatusEnum.UnknownError;
                            named.loadError.error = error;
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            return named as any;
                        }
                    }
                }
            }
            throw new Error(error);
        }
    }

    public async postToModel(value: T): Promise<void> {
        return;
    }

    public toFabricCopilotContext(model: T): D {
        return undefined;
    }

    public fromFabricCopilotContext(context: D, model?: T): T {
        return model;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    protected abstract _toResource(named: T, ...other: any[]): U;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    protected abstract _toModel(resource: U, ...other: any[]): T;

    protected _logError(error: string): void {
        const nullableLogger = this.getNullableLogger();
        if (nullableLogger) {
            nullableLogger.error(BaseAdapter.className, error);
        }
    }

    protected _logWarning(msg: string): void {
        const nullableLogger = this.getNullableLogger();
        if (nullableLogger) {
            nullableLogger.warn(BaseAdapter.className, msg);
        }
    }

    private _cachedLogger: ILogger | undefined;

    protected getNullableLogger(): ILogger | undefined {
        if (this._cachedLogger) {
            return this._cachedLogger;
        }

        if (this.context?.contextData) {
            this._cachedLogger = BaseAdapter.getCachedLoggerSync(this.context.contextData);
        }

        return this._cachedLogger;
    }
}
